// ============================================================================
// The hiring form's building picker.
//
// A persistent search box over one list of every building the picker's source
// returns, grouped into three tiers with headings. Search runs across all
// three tiers, so typing a name finds the building whichever tier it sits in.
//
// A native <select> with <optgroup> would give the grouping for free but not
// the search — a native select's type-ahead only matches the start of an
// option's text, which is useless for "Overland Park Regional" when you type
// "regional", and it cannot match on facility id at all. Hence the listbox.
// ============================================================================

import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { tierFacilities, searchTiers, type TierFacility } from './facility-tiers';

export function FacilityTierPicker({
  facilities,
  value,
  onChange,
  hourlyIds,
  accountWideLabel = 'Account-wide — no specific building',
  disabled,
}: {
  facilities: readonly TierFacility[];
  /** '' means the account-wide requisition. */
  value: string;
  onChange: (facilityId: string) => void;
  hourlyIds?: readonly string[];
  accountWideLabel?: string;
  disabled?: boolean;
}) {
  const [query, setQuery] = useState('');

  const tiers = useMemo(() => tierFacilities(facilities, hourlyIds), [facilities, hourlyIds]);
  const shown = useMemo(() => searchTiers(tiers, query), [tiers, query]);
  const total = useMemo(
    () => shown.reduce((n, t) => n + t.facilities.length, 0),
    [shown],
  );

  const selected = useMemo(
    () => facilities.find((f) => f.facility_id === value) ?? null,
    [facilities, value],
  );

  return (
    <div className="rounded-md border border-tcs-border bg-tcs-raised">
      <div className="flex items-center gap-2 border-b border-tcs-border px-2 py-1.5">
        <Search className="h-4 w-4 shrink-0 text-tcs-text-muted" aria-hidden />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={disabled}
          placeholder="Search every building by name or id"
          aria-label="Search buildings"
          className="w-full bg-transparent text-sm text-tcs-text outline-none placeholder:text-tcs-text-muted"
        />
      </div>

      <div role="listbox" aria-label="Building" className="max-h-64 overflow-y-auto p-1">
        {/* The account-wide row is a real answer, not an escape hatch: 9 of the
            10 facility-less requisitions on record are account-wide. It stays
            at the top and out of the tiers, and the search does not hide it —
            it is not a building, so it has no name to match. */}
        <Row
          selected={value === ''}
          disabled={disabled}
          onSelect={() => onChange('')}
          title={accountWideLabel}
        />

        {shown.map((tier) => (
          <div key={tier.key} className="mt-1">
            <p className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-tcs-text-muted">
              {tier.label}
            </p>
            {tier.facilities.map((f) => (
              <Row
                key={f.facility_id}
                selected={f.facility_id === value}
                disabled={disabled}
                onSelect={() => onChange(f.facility_id)}
                title={f.facility_name ?? f.facility_id}
                subtitle={f.status && f.status !== 'Active' ? f.status : undefined}
              />
            ))}
          </div>
        ))}

        {total === 0 && (
          <p className="px-2 py-3 text-sm text-tcs-text-muted">
            No building matches “{query.trim()}”. Clear the search to see all{' '}
            {facilities.length} of them.
          </p>
        )}
      </div>

      {selected && (
        <p className="border-t border-tcs-border px-2 py-1.5 text-xs text-tcs-text-muted">
          Selected: <span className="text-tcs-text">{selected.facility_name ?? selected.facility_id}</span>
        </p>
      )}
    </div>
  );
}

function Row({
  selected,
  disabled,
  onSelect,
  title,
  subtitle,
}: {
  selected: boolean;
  disabled?: boolean;
  onSelect: () => void;
  title: string;
  subtitle?: string;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      disabled={disabled}
      onClick={onSelect}
      className={`flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm ${
        selected ? 'bg-tcs-bg-muted font-medium text-tcs-text' : 'text-tcs-text hover:bg-tcs-bg-muted'
      }`}
    >
      <span>{title}</span>
      {subtitle && <span className="shrink-0 text-xs text-tcs-text-muted">{subtitle}</span>}
    </button>
  );
}
